import { SESSION_STORAGE_JWT_TOKEN } from "../pages/Login/constants"
import { clearSession, readTokenPayload } from "./session"

// The token is validated BEFORE sending it (ROADMAP debt 2.5): this module
// used to read the raw token from sessionStorage and send the backend expired
// or malformed tokens that `readTokenPayload` would have rejected. It now goes
// through session.ts: if the token is not usable, the session is cleared and
// the user is returned to login without the API ever being called.
const readValidToken = (): string | null => {
    const token = sessionStorage.getItem(SESSION_STORAGE_JWT_TOKEN);
    return readTokenPayload(token) ? token : null;
};

// Return to login from a function.
//
// This is neither a component nor a hook: there is no `useNavigate` to use, so
// the redirect goes through `window.location.assign`. The full reload that
// implies is even desirable here — it discards all in-memory state from the
// expired session.
//
// The returned promise never resolves, on purpose: the page is about to be
// unloaded. Resolving would force inventing a response; rejecting would make
// the error states of screens that are about to disappear flicker.
const expireSession = (): Promise<never> => {
    clearSession();
    window.location.assign("/login");
    return new Promise<never>(() => {});
};

export const archivosFetch = (url: string, options?: Record<string, unknown>) => {
    const token = readValidToken();
    if (!token) return expireSession();

    return fetch(url, {
        ...options,
        headers: {
            ...options?.headers as Record<string, string>,
            "authorization": `Bearer ${token}`
        }
    })
}

// Upload with progress.
//
// `fetch` does not expose upload progress, and the page list of the archiving
// wizard shows a per-row bar while each capture uploads. That is the only
// reason this call goes through XMLHttpRequest: the token is validated and
// injected here, just like in archivosFetch, so that there remains a single
// place that reads it.
//
// Resolves with the status and the JSON body (if any) whatever the status,
// like `fetch`; it only rejects on a network failure or cancellation.

export type UploadResult = {
    status: number;
    ok: boolean;
    body: unknown;
};

export const archivosUpload = (
    url: string,
    form: FormData,
    options?: {
        onProgress?: (fraction: number) => void;
        signal?: AbortSignal;
    }
): Promise<UploadResult> => {
    const token = readValidToken();
    if (!token) return expireSession();

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url);
        xhr.setRequestHeader("authorization", `Bearer ${token}`);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && options?.onProgress) {
                options.onProgress(event.loaded / event.total);
            }
        };

        xhr.onload = () => {
            let body: unknown;
            try {
                body = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
            } catch (_) {
                body = undefined;
            }
            resolve({
                status: xhr.status,
                ok: xhr.status >= 200 && xhr.status < 300,
                body,
            });
        };
        xhr.onerror = () => reject(new TypeError("Network request failed"));
        xhr.onabort = () => reject(new DOMException("Upload aborted", "AbortError"));

        if (options?.signal) {
            if (options.signal.aborted) {
                reject(new DOMException("Upload aborted", "AbortError"));
                return;
            }
            options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
        }

        xhr.send(form);
    });
};
